import { useState } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { errMsg, http } from "@/lib/api";
import { Missing, Panel } from "@/components/host/HostOverview";

const GROUPS = [
  ["Opening screen", [["opening_eyebrow", "Opening badge"], ["opening_line", "Opening line"]]],
  ["Title & welcome", [["title", "Event title"], ["tagline", "Short description"], ["welcome_heading", "Welcome heading"], ["message", "Host message", true]]],
  ["Hosts", [["host_names", "Host names"], ["host_note", "Host note"]]],
  ["Kab? (date & time)", [["date", "Date (YYYY-MM-DD)"], ["date_note", "Date note"], ["start_time", "Start time (24h HH:MM)"], ["start_note", "Start time note"], ["end_time", "End time (24h HH:MM), used for calendar invites only"], ["end_date", "End date (YYYY-MM-DD), set the next day if the party runs past midnight"], ["timezone", "Timezone (e.g. Australia/Sydney)"]]],
  ["Kahan? (venue)", [["venue_title", "Venue heading"], ["venue_label", "Venue note"], ["address", "Full address (hidden from guests; used for calendar & map fallback)"], ["map_url", "Google Maps or WhatsApp location link (recommended)"]]],
  ["Mehfil ke Usool (house rules)", [["rules_title", "Section title"], ["rules_subtitle", "Subtitle"]]],
  ["Contact", [["contact_label", "Contact title"], ["contact_phone", "Contact phone"], ["contact_note", "Contact note"]]],
  ["Links", [["public_url", "Public invitation link (used in shares & calendar)"]]],
];
const ICONS = [["tray", "Brass tray"], ["mithai", "Mithai plate"], ["cards", "Playing cards"], ["lights", "String lights"]];
const RULE_ICONS = [["cheers", "Cheers"], ["kurta", "Kurta"], ["phone", "Phone"], ["dice", "Dice"]];

function RulesEditor({ items, onChange }) {
  const upd = (i, k, v) => onChange(items.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="flex flex-col gap-3">
      {items.map((r, i) => (
        <div key={r.id} className="grid gap-2 rounded-2xl border-2 border-[var(--navy)] bg-[var(--ivory)] p-3 md:grid-cols-12" data-testid={`host-rule-${i}`}>
          <select aria-label="Illustration" className="field md:col-span-2" value={r.icon} onChange={(e) => upd(i, "icon", e.target.value)}>{RULE_ICONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          <input aria-label="Rule heading" className="field md:col-span-4" value={r.title} maxLength={80} onChange={(e) => upd(i, "title", e.target.value)} />
          <input aria-label="Rule text" className="field md:col-span-5" value={r.text} maxLength={300} onChange={(e) => upd(i, "text", e.target.value)} />
          <button type="button" className="btn-ghost min-h-[44px] px-3 md:col-span-1" onClick={() => onChange(items.filter((_, j) => j !== i))} aria-label="Remove rule"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <button type="button" className="btn-ghost self-start" onClick={() => onChange([...items, { id: `r${Date.now()}`, icon: "cheers", title: "New rule", text: "" }])}><Plus className="h-4 w-4" /> Add rule</button>
    </div>
  );
}

function Activities({ items, onChange }) {
  const upd = (i, k, v) => onChange(items.map((a, j) => (j === i ? { ...a, [k]: v } : a)));
  const move = (i, d) => {
    const n = [...items];
    [n[i], n[i + d]] = [n[i + d], n[i]];
    onChange(n);
  };
  return (
    <div className="flex flex-col gap-3">
      {items.map((a, i) => (
        <div key={a.id} className="grid gap-2 rounded-2xl border border-[rgba(181,148,82,.45)] bg-[var(--ivory)] p-3 md:grid-cols-12" data-testid={`host-activity-${i}`}>
          <select aria-label="Illustration" className="field md:col-span-2" value={a.icon} onChange={(e) => upd(i, "icon", e.target.value)}>{ICONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          <input aria-label="Title" className="field md:col-span-3" value={a.title} maxLength={80} onChange={(e) => upd(i, "title", e.target.value)} />
          <input aria-label="Description" className="field md:col-span-4" value={a.description} maxLength={240} onChange={(e) => upd(i, "description", e.target.value)} />
          <input aria-label="Time (optional)" placeholder="Time (optional)" className="field md:col-span-1" value={a.time} maxLength={40} onChange={(e) => upd(i, "time", e.target.value)} />
          <div className="flex gap-1 md:col-span-2">
            <button type="button" className="btn-ghost min-h-[44px] px-3" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up"><ArrowUp className="h-4 w-4" /></button>
            <button type="button" className="btn-ghost min-h-[44px] px-3" disabled={i === items.length - 1} onClick={() => move(i, 1)} aria-label="Move down"><ArrowDown className="h-4 w-4" /></button>
            <button type="button" className="btn-ghost min-h-[44px] px-3" onClick={() => onChange(items.filter((_, j) => j !== i))} aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>
      ))}
      <button type="button" className="btn-ghost self-start" onClick={() => onChange([...items, { id: `a${Date.now()}`, icon: "tray", title: "New moment", description: "", time: "" }])}><Plus className="h-4 w-4" /> Add activity</button>
    </div>
  );
}

export default function HostEvent({ eventData, setEventData }) {
  const [form, setForm] = useState(eventData.event);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const keys = GROUPS.flatMap(([, f]) => f.map(([k]) => k));
      const body = Object.fromEntries(keys.map((k) => [k, form[k] ?? ""]));
      body.activities = form.activities;
      body.rules = form.rules;
      const r = await http.put("/host/event", body);
      setEventData(r.data);
      setForm(r.data.event);
      toast.success("Invitation updated");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className="flex flex-col gap-6" data-testid="host-event-form">
      <Missing items={eventData.missing} />
      {GROUPS.map(([title, fields]) => (
        <Panel key={title} title={title}>
          <div className="grid gap-4 md:grid-cols-2">
            {fields.map(([k, label, long]) => (
              <div key={k} className={long ? "md:col-span-2" : ""}>
                <label htmlFor={`ev-${k}`} className="text-sm font-semibold">{label}</label>
                {long ? (
                  <textarea id={`ev-${k}`} rows={3} className="field mt-1" value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} />
                ) : (
                  <input id={`ev-${k}`} className="field mt-1" value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`ev-input-${k}`} />
                )}
              </div>
            ))}
          </div>
        </Panel>
      ))}
      <Panel title="House rules">
        <RulesEditor items={form.rules || []} onChange={(v) => set("rules", v)} />
      </Panel>
      <Panel title="A glimpse of the evening">
        <Activities items={form.activities || []} onChange={(v) => set("activities", v)} />
      </Panel>
      <div className="sticky bottom-3 z-10">
        <button type="submit" className="btn-primary w-full sm:w-auto" disabled={busy} data-testid="host-event-save-button">{busy ? "Saving…" : "Save invitation details"}</button>
      </div>
    </form>
  );
}
